import { MenuItemOption } from "@components/common/mobile/mobile-types";
import { DeleteMenuItem } from "@components/project/delete-menu-item";
import { Snapshot } from "@custom-types/base-types";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import {
  DeleteIcon,
  HolobuilderIcon,
  SelectionIcon,
  ShareLinkIcon,
  ViewerIcon,
} from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import {
  getOpenSnapshotHref,
  trackOpenSnapshot,
} from "@pages/project-details/project-snapshots/snapshot-utils";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { commonStrings } from "@src/constants/common-strings";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { toggleItemSelection } from "@utils/array-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SnapshotShareMenuItem } from "@pages/project-details/project-snapshots/snapshot-share-menu-item";

/**
 * Custom hook to get the menu options for a snapshot.
 * Provides actions for viewing details, opening in different targets, sharing, and deleting a snapshot.
 */
export function useSnapshotMenuOptions(): MenuItemOption<Snapshot>[] {
  const { trackAsyncEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);

  const snapshotLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const { isScreenSmOrSmaller } = useMediaQueryList();

  /** Tracks and opens a snapshot in the given launch target */
  function openSnapshot(
    snapshot: Snapshot,
    openTarget: ProjectLaunchTarget
  ): void {
    void trackOpenSnapshot({
      openTarget,
      trackAsyncEvent,
      openFrom: "snapshots-menu-options",
      accessLevel: snapshot.accessLevel,
      createdAt: snapshot.createdAt,
    });

    window.open(
      getOpenSnapshotHref({ snapshotId: snapshot.id, openTarget }),
      "_self"
    );
  }

  const menuOptions: MenuItemOption<Snapshot>[] = [
    {
      label: "Select snapshot",
      Icon: SelectionIcon,
      onSelect: (snapshot) => {
        const newSelectedRowIds = toggleItemSelection(
          selectedRowIds,
          snapshot.id
        );
        dispatch(setInitialFetchingItems(newSelectedRowIds));
      },
      shouldShow: (snapshot) =>
        isScreenSmOrSmaller && isUserSnapshotAdmin(snapshot),
      shouldShowDividerAfter: true,
    },
    {
      label: isScreenSmOrSmaller
        ? commonStrings.SPHERE_XG
        : `Open in ${commonStrings.SPHERE_XG}`,
      Icon: ViewerIcon,
      onSelect: (snapshot) => {
        openSnapshot(snapshot, ProjectLaunchTarget.sphereViewer);
      },
      getHrefUrl: (snapshot) => {
        return getOpenSnapshotHref({
          snapshotId: snapshot.id,
          openTarget: ProjectLaunchTarget.sphereViewer,
        });
      },
      shouldHandleNavigation: true,
    },
    {
      label: isScreenSmOrSmaller
        ? commonStrings.HOLOBUILDER_PRODUCT_NAME
        : `Open in ${commonStrings.HOLOBUILDER_PRODUCT_NAME}`,
      Icon: HolobuilderIcon,
      onSelect: (snapshot) => {
        openSnapshot(snapshot, ProjectLaunchTarget.webEditor);
      },
      getHrefUrl: (snapshot) => {
        return getOpenSnapshotHref({
          snapshotId: snapshot.id,
          openTarget: ProjectLaunchTarget.webEditor,
        });
      },
      shouldHandleNavigation: true,
      shouldShow: () => {
        return snapshotLaunchSettings.shouldShowOpenInWebEditorBtn;
      },
      shouldShowDividerAfter: true,
    },
    {
      label: "Share",
      Icon: ShareLinkIcon,
      component: (snapshot) => <SnapshotShareMenuItem snapshot={snapshot} />,
      shouldShow: () => isScreenSmOrSmaller,
    },
    {
      label: "Delete",
      Icon: DeleteIcon,
      component: (snapshot) => (
        <DeleteMenuItem type="snapshot" entity={snapshot} />
      ),
      shouldShow: isUserSnapshotAdmin,
    },
  ];

  return menuOptions;
}
